// src/pages/registration/Registration.js
import React, { useState, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import SignatureCanvas from "react-signature-canvas"
import apiPublic from "../../services/apiPublic"
import { useTranslation } from "react-i18next"
import "../../assets/css/Registration.css"

export default function Registration() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const barcodeId = searchParams.get("barcodeId") || ""
  const [loaded, setLoaded] = useState(false)
  const [companyName, setCompanyName] = useState("")
  const [questionnaire, setQuestionnaire] = useState(null)
  const [status, setStatus] = useState("")
  const [name, setName] = useState("")
  const [phonePrefix, setPhonePrefix] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [preferredLanguage, setPreferredLanguage] = useState("")
  const [languages, setLanguages] = useState([])
  const [signatureData, setSignatureData] = useState("")
  const [consentStoreData, setConsentStoreData] = useState(false)
  const [consentReadEverything, setConsentReadEverything] = useState(false)
  const [missingFields, setMissingFields] = useState([])
  const [registered, setRegistered] = useState(false)
  const [answers, setAnswers] = useState({})
  const sigCanvasRef = useRef(null)

  const europeanPrefixes = [
    { code: "+49", label: `${t("registration.countryNames.germany")} (+49)` },
    { code: "+48", label: `${t("registration.countryNames.poland")} (+48)` },
    { code: "+420", label: `${t("registration.countryNames.czechRepublic")} (+420)` },
    { code: "+421", label: `${t("registration.countryNames.slovakia")} (+421)` },
    { code: "+36", label: `${t("registration.countryNames.hungary")} (+36)` },
    { code: "+359", label: `${t("registration.countryNames.bulgaria")} (+359)` },
    { code: "+40", label: `${t("registration.countryNames.romania")} (+40)` },
    { code: "+380", label: `${t("registration.countryNames.ukraine")} (+380)` },
    { code: "+44", label: `${t("registration.countryNames.uk")} (+44)` },
    { code: "+43", label: `${t("registration.countryNames.austria")} (+43)` },
    { code: "+39", label: `${t("registration.countryNames.italy")} (+39)` },
    { code: "+34", label: `${t("registration.countryNames.spain")} (+34)` },
    { code: "+33", label: `${t("registration.countryNames.france")} (+33)` },
    { code: "+46", label: `${t("registration.countryNames.sweden")} (+46)` },
    { code: "+45", label: `${t("registration.countryNames.denmark")} (+45)` },
    { code: "+41", label: `${t("registration.countryNames.switzerland")} (+41)` },
    { code: "+31", label: `${t("registration.countryNames.netherlands")} (+31)` },
    { code: "+32", label: `${t("registration.countryNames.belgium")} (+32)` },
    { code: "+354", label: `${t("registration.countryNames.iceland")} (+354)` },
    { code: "+7", label: `${t("registration.countryNames.russia")} (+7)` }
  ]

  useEffect(() => {
    loadFormData()
  }, [])

  async function loadFormData() {
    try {
      const r = await apiPublic.get("/registration/form", { params: { barcodeId } })
      setCompanyName(r.data.companyName || "")
      setQuestionnaire(r.data.questionnaire || null)
      const langRes = await apiPublic.get("/language")
      setLanguages(langRes.data.languages)
      setLoaded(true)
    } catch (err) {
      setStatus("Error loading form")
    }
  }

  function clearSignature() {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear()
    }
    setSignatureData("")
  }

  function saveSignature() {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      const dataURL = sigCanvasRef.current.getCanvas().toDataURL("image/png")
      setSignatureData(dataURL)
    }
  }

  function handleFieldChange(label, value) {
    setAnswers(prev => ({ ...prev, [label]: value }))
  }

  function getFinalImageUrl(img) {
    if (!img) return ""
    if (img.startsWith("http")) return img
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5111/api"
    return baseUrl.replace("/api", "") + img
  }

  function getTranslatedQuestionnaireName() {
    if (!questionnaire) return ""
    if (questionnaire.nameJson) {
      try {
        const parsed = JSON.parse(questionnaire.nameJson)
        const key = Object.keys(parsed)[0]
        return t(key) || questionnaire.name
      } catch (e) {
        return questionnaire.name
      }
    }
    return questionnaire.name || t("registration.defaultTitle")
  }

  function getTranslatedQuestionnaireDescription() {
    if (!questionnaire) return ""
    if (questionnaire.descriptionJson) {
      try {
        const parsed = JSON.parse(questionnaire.descriptionJson)
        const key = Object.keys(parsed)[0]
        const translated = t(key)
        if (translated && translated !== key) return replaceLineBreaks(translated)
        return replaceLineBreaks(questionnaire.description)
      } catch (e) {
        return replaceLineBreaks(questionnaire.description)
      }
    }
    return replaceLineBreaks(questionnaire.description || "")
  }

  function replaceLineBreaks(text) {
    if (!text) return ""
    return text.split("\n").map((line, idx) => (
      <React.Fragment key={idx}>
        {line}
        <br />
      </React.Fragment>
    ))
  }

  function getTranslatedFieldLabel(field) {
    if (field.labelJson) {
      try {
        const parsed = JSON.parse(field.labelJson)
        const key = Object.keys(parsed)[0]
        return t(key) || field.label
      } catch (err) {
        return field.label
      }
    }
    return field.label
  }

  function getTranslatedFieldOption(optString) {
    try {
      const parsed = JSON.parse(optString)
      const key = Object.keys(parsed)[0]
      return t(key) || parsed[key]
    } catch (e) {
      return ""
    }
  }

  async function handleRegister() {
    const needed = []
    const cleaned = phoneNumber.replace(/^0+/, "")
    const finalPhone = phonePrefix + cleaned
    if (!barcodeId) needed.push("barcodeId")
    if (!name) needed.push("name")
    if (!phonePrefix || !cleaned) needed.push("phoneNumber")
    if (!signatureData) needed.push("signature")
    if (!consentStoreData) needed.push("consentStoreData")
    if (!consentReadEverything) needed.push("consentReadEverything")
    setMissingFields(needed)
    if (needed.length) {
      setStatus(t("registration.missingRequiredFields"))
      return
    }
    if (!/^\+[1-9]\d{7,14}$/.test(finalPhone)) {
      setStatus(t("registration.invalidPhoneFormat"))
      return
    }
    try {
      const finalAnswers = []
      if (questionnaire && questionnaire.fields) {
        for (let i = 0; i < questionnaire.fields.length; i++) {
          const field = questionnaire.fields[i]
          const val = answers[field.label] || (field.type === "checkbox" ? false : "")
          const valObj = {}
          valObj[field.label] = val
          finalAnswers.push({
            fieldLabel: field.label,
            fieldLabelJson: field.labelJson || "",
            fieldType: field.type,
            value: val,
            valueJson: JSON.stringify(valObj)
          })
        }
      }
      const body = {
        name,
        phone: finalPhone,
        preferredLanguage,
        signature: signatureData,
        consentStoreData,
        consentReadEverything,
        answers: finalAnswers
      }
      const res = await apiPublic.post("/registration/submit", body, {
        params: { barcodeId }
      })
      if (res.data.error) {
        setStatus(res.data.error)
      } else {
        setStatus(t("registration.registrationComplete"))
        setRegistered(true)
      }
    } catch (err) {
      setStatus(t("registration.submissionFailed"))
    }
  }

  if (!loaded) {
    return <div>{t("registration.loading")}...</div>
  }

  if (!questionnaire && !registered) {
    return (
      <div>
        <h2>{t("registration.title")}</h2>
        <p>{t("registration.noQuestionnaireFound")}</p>
        <p>{status}</p>
      </div>
    )
  }

  if (!registered) {
    return (
      <div className="container page-container">
        <h2>{getTranslatedQuestionnaireName()}</h2>
        <p>{getTranslatedQuestionnaireDescription()}</p>
        {questionnaire?.images &&
          questionnaire.images.map((img, i) => {
            const finalSrc = getFinalImageUrl(img)
            return (
              <div key={i} className="registration-image-container">
                <img
                  src={finalSrc}
                  alt={`questionnaire-img-${i}`}
                  className="responsive-image"
                />
              </div>
            )
          })}
        <div>* {t("registration.mandatory.fields")}<br/><br/></div>
        <div
          className={`form-group ${
            missingFields.includes("name") ? "missing-field" : ""
          }`}
        >
          <label>{t("registration.name")} *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <div
          className={`phone-group ${
            missingFields.includes("phoneNumber") ? "missing-field" : ""
          }`}
        >
          <label>{t("registration.phoneNumber")} *</label>
          <div className="phone-container">
            <select
              className="phone-prefix"
              value={phonePrefix}
              onChange={e => setPhonePrefix(e.target.value)}
            >
              <option value="">{t("registration.selectPrefix")}</option>
              {europeanPrefixes.map(p => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
            <input
              className="phone-input"
              type="tel"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label>{t("registration.preferredLanguage")} *</label>
          <select
            value={preferredLanguage}
            onChange={e => setPreferredLanguage(e.target.value)}
          >
            <option value="">{t("registration.chooseLanguage")}</option>
            {languages.map(l => (
              <option key={l._id} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        {questionnaire?.fields &&
          questionnaire.fields.map((field, i) => (
            <div key={i} style={{ marginTop: "1rem" }}>
              {field.type === "checkbox" ? (
                <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                  <input
                    type="checkbox"
                    checked={answers[field.label] || false}
                    onChange={e => handleFieldChange(field.label, e.target.checked)}
                  />
                  {getTranslatedFieldLabel(field)}
                  {field.required ? " *" : ""}
                </label>
              ) : (
                <>
                  <label className="form-group-label">
                    {getTranslatedFieldLabel(field)}
                    {field.required ? " *" : ""}
                  </label>
                  {field.type === "text" && (
                    <input
                      type="text"
                      value={answers[field.label] || ""}
                      onChange={e => handleFieldChange(field.label, e.target.value)}
                    />
                  )}
                  {field.type === "textarea" && (
                    <textarea
                      value={answers[field.label] || ""}
                      onChange={e => handleFieldChange(field.label, e.target.value)}
                    />
                  )}
                  {field.type === "select" && (
                    <select
                      value={answers[field.label] || ""}
                      onChange={e => handleFieldChange(field.label, e.target.value)}
                    >
                      <option value="">{t("registration.selectOption")}</option>
                      {field.optionsJson && field.optionsJson.length
                        ? field.optionsJson.map((optString, idx) => (
                            <option
                              key={idx}
                              value={getTranslatedFieldOption(optString)}
                            >
                              {getTranslatedFieldOption(optString)}
                            </option>
                          ))
                        : field.options.map((opt, idx) => (
                            <option key={idx} value={opt}>
                              {opt}
                            </option>
                          ))}
                    </select>
                  )}
                  {field.type === "number" && (
                    <input
                      type="number"
                      value={answers[field.label] || ""}
                      onChange={e => handleFieldChange(field.label, e.target.value)}
                    />
                  )}
                  {field.type === "date" && (
                    <input
                      type="date"
                      value={answers[field.label] || ""}
                      onChange={e => handleFieldChange(field.label, e.target.value)}
                    />
                  )}
                </>
              )}
            </div>
          ))}
        <h4 className="mt-2">{t("registration.dataConsents")}</h4>
        <div
          className={`mb-1 ${
            missingFields.includes("consentStoreData") ? "missing-field" : ""
          }`}
        >
          <label>
            <input
              type="checkbox"
              checked={consentStoreData}
              onChange={e => setConsentStoreData(e.target.checked)}
            />{" "}
            {t("registration.storeMyData")}
          </label>
        </div>
        <div
          className={`mb-1 ${
            missingFields.includes("consentReadEverything") ? "missing-field" : ""
          }`}
        >
          <label>
            <input
              type="checkbox"
              checked={consentReadEverything}
              onChange={e => setConsentReadEverything(e.target.checked)}
            />{" "}
            {t("registration.iHaveReadEverything")}
          </label>
        </div>
        <div
          className={`signature-container ${
            missingFields.includes("signature") ? "missing-field" : ""
          }`}
        >
          <label>{t("registration.signature")}</label>
          <SignatureCanvas
            ref={sigCanvasRef}
            penColor="black"
            canvasProps={{ className: "sigCanvas" }}
            onEnd={saveSignature}
          />
        </div>
        <div style={{ marginTop: "1rem" }}>
          <button style={{ marginRight: 10 }} onClick={clearSignature}>
            {t("registration.clearSignature")}
          </button>
          <button onClick={handleRegister}>{t("registration.register")}</button>
        </div>
        <p style={{ marginTop: "1rem" }}>{status}</p>
      </div>
    )
  }

  return (
    <div className="container">
      <h2>{t("registration.complete")}</h2>
      <p>{status}</p>
      <p>{t("registration.registrationIsComplete")}</p>
    </div>
  )
}